'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useModel } from '@/context/ModelContext';

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0].value;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className={`font-mono font-bold ${val >= 0 ? 'text-primary' : 'text-danger'}`}>
        ₹{val.toFixed(2)} Cr
      </p>
      <p className="text-muted-foreground mt-0.5">
        {val >= 0 ? '✓ Recovered' : 'Investment outstanding'}
      </p>
    </div>
  );
}

export default function PaybackChart() {
  const { model } = useModel();

  let cum = 0;
  const data = model.years.map((y) => {
    cum += y.projectFcf;
    return {
      name: y.year === 0 ? 'Yr 0' : `Yr ${y.year}`,
      cumFcf: parseFloat((cum / 10000000).toFixed(2)),
    };
  });

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="gradPayback" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.3} />
            <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="gradPaybackNeg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--danger)" stopOpacity={0.15} />
            <stop offset="95%" stopColor="var(--danger)" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)', fontFamily: 'IBM Plex Mono' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `₹${v}Cr`}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine
          y={0}
          stroke="var(--danger)"
          strokeDasharray="4 4"
          strokeWidth={1.5}
          label={{
            value: 'Breakeven',
            position: 'insideTopRight',
            fontSize: 10,
            fill: 'var(--danger)',
          }}
        />
        <Area
          type="monotone"
          dataKey="cumFcf"
          stroke="var(--primary)"
          strokeWidth={2.5}
          fill="url(#gradPayback)"
          dot={{ fill: 'var(--primary)', r: 3, strokeWidth: 0 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
